import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Button } from 'react-bootstrap';
import Select from 'react-select';

const IFBatchReportPage = () => {
  const location = useLocation();
  const Navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const batch = queryParams.get('batch');
  const role = sessionStorage.getItem("role")

  const [reportData, setReportData] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentName, setStudentName] = useState('');

  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  useEffect(() => {
    if (batch) {
      fetchBatchReport();
    }
  }, [batch, studentName]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!batch) return;
      try {
        setStudentsLoading(true);
        const response = await fetch(`${LMS}/api/get-students-by-batch/${parseInt(batch)}`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const result = await response.json();
        const list = Array.isArray(result?.data) ? result.data : [];
        setStudents(list.map(s => ({
          value: `${s.first_name} ${s.last_name}`.trim(),
          label: `${s.first_name} ${s.last_name}`.trim()
        })));
      } catch (err) {
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudents();
  }, [batch, LMS, token]);

  const fetchBatchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${LMS}/api/batch/report`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          batch_id: parseInt(batch),
          ...(studentName ? { student_name: studentName } : {})
        })
      });

      const result = await response.json();

      if (result.status) {
        setReportData(result.data);
        if (result.data.length > 0) {
          setBatchName(result.data[0].batch_name);
        }
      } else {
        setError(result.message || 'Failed to fetch batch report');
      }
    } catch (err) {
      setError('An error occurred while fetching the report');
      console.error('Error fetching batch report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      setExporting(true);

      const response = await fetch(`${LMS}/api/batch/batch-course-progress/export`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          batch_id: parseInt(batch),
          type: type
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch_report_${batch}.${type === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to export data');
      }
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('An error occurred while exporting');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Batch Report" breadcrumbs={[
        { label: "Dashboard", path: "/teacher-dashboard" },
        { label: "Batch", path: '/teacher-batches' },
        { label: "Batch Report", active: true }
      ]} />

      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
              <h5 className="mb-0 text-primary">
                {batchName && `${batchName} - `}Course Progress Report
              </h5>
              <div className="d-flex flex-column gap-2 w-md-auto">
                <div className="btn-group" role="group">
                  <button
                    className="btn btn-success btn-sm flex-fill"
                    onClick={() => handleExport('excel')}
                    disabled={exporting || loading}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    <span className="d-none d-sm-inline">Export Excel</span>
                    <span className="d-inline d-sm-none">Excel</span>
                  </button>
                  <button
                    className="btn btn-info btn-sm flex-fill"
                    onClick={() => handleExport('csv')}
                    disabled={exporting || loading}
                  >
                    <i className="ri-file-text-line me-1"></i>
                    <span className="d-none d-sm-inline">Export CSV</span>
                    <span className="d-inline d-sm-none">CSV</span>
                  </button>
                </div>
                <Button
                  variant="link"
                  className="p-0 text-decoration-none text-primary fw-semibold"
                  onClick={() => Navigate(role === 'Institute' ? '/institute-batch' : '/teacher-batches')}
                >
                  <i className="fas fa-arrow-left me-1"></i> Back to Batch List
                </Button>
              </div>
            </div>
            <div className="card-body border-top">
              <div className="row g-2 align-items-end">
                <div className="col-md-4 col-lg-3">
                  <label className="form-label fw-semibold">Student</label>
                  <Select
                    options={[{ value: '', label: 'All Students' }, ...students]}
                    value={[{ value: '', label: 'All Students' }, ...students].find(opt => opt.value === studentName) || { value: '', label: 'All Students' }}
                    onChange={(selected) => setStudentName(selected ? selected.value : '')}
                    placeholder="All Students"
                    isClearable
                    isDisabled={studentsLoading}
                  />
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={() => setStudentName('')}
                    disabled={studentsLoading || !studentName}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
            <div>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading batch report...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </div>
              ) : reportData.length === 0 ? (
                <div className="alert alert-info" role="alert">
                  <i className="ri-information-line me-2"></i>
                  No data available for this batch.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Student Name</th>
                        <th>Course Name</th>
                        <th>Video Progress</th>
                        <th>Assignments Progress</th>
                        <th>Assignment Score</th>
                        <th>Assessment Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((row, index) => {
                        const studentName = row?.["Student Name"] ?? `${row?.first_name || ''} ${row?.last_name || ''}`.trim();
                        const courseName = row?.["Course Name"] ?? row?.course_name ?? '';
                        const videoProgress = row?.["Video Progress"] ?? (
                          row?.completed_videos !== undefined && row?.total_videos !== undefined
                            ? `${row.completed_videos}/${row.total_videos}`
                            : ''
                        );
                        const assignmentsProgress = row?.["Assignments Progress"] ?? (
                          row?.assignment_completed !== undefined && row?.assignment_total !== undefined
                            ? `${row.assignment_completed}/${row.assignment_total}`
                            : ''
                        );
                        const assignmentScore = row?.["Assignment Score"] ?? row?.assignment_score ?? null;
                        const assessmentScore = row?.["Assessment Score"] ?? (
                          row?.quiz_achieved_points !== undefined && row?.quiz_total_points !== undefined
                            ? `${row.quiz_achieved_points} / ${row.quiz_total_points}`
                            : row?.quiz_score ?? null
                        );

                        return (
                          <tr key={index}>
                            <td className='text-semibold'>{studentName}</td>
                            <td>{courseName}</td>
                            <td>{videoProgress}</td>
                            <td>{assignmentsProgress}</td>
                            <td>{assignmentScore ?? 'NA'}</td>
                            <td>{assessmentScore ?? 'NA'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IFBatchReportPage;
